import * as React from 'react';
import autoAnimate from '@formkit/auto-animate';
import { ChapterCatalogData } from '@core/models/catalog';
import { useChapterRowUiContext } from './chapter-row-ui-model';
import { Button } from '@naan/primitives/button';
import { observer } from 'mobx-react';
import { useNavigateToStudy } from 'lib/hooks/use-navigate-to-study';
import { PlayerMode } from '@common/misc-types';
import { ArrowLeftIcon } from '@naan/icons/arrow-left-icon';

import __ from '@core/lib/localization';
import { AppFactory } from '@app/app-factory';
// import { maybePresentEndOfStoryCtaDialog } from 'components/cta/end-of-story-cta-dialog';

const getButtonLabel = (
  mode: 'study' | 'listen' | 'whoa' | 'completed',
  midListen: boolean
) => {
  const labels = {
    study: midListen
      ? __('Continue studying', 'continueStudying')
      : __('Begin studying chapter', 'beginStudyingChapter'),
    listen: midListen
      ? __('Continue listening', 'continueListening')
      : __('Begin listening', 'beginListening'),
    whoa: __('Go back', 'goBack'),
    completed: __('Mark chapter complete', 'markChapterComplete'),
  };
  return labels[mode];
};

type ButtonPresentation = typeof Button['defaultProps']['presentation'];

export const ChapterActionButton = observer(
  ({
    chapter,
    viaDetail,
    presentation,
  }: {
    chapter: ChapterCatalogData;
    viaDetail: boolean;
    presentation?: ButtonPresentation;
  }) => {
    const navigateToStudy = useNavigateToStudy();
    const model = useChapterRowUiContext();
    const { currentStudyMode: mode } = model;
    const whoa = mode === 'completed' && !chapter.isFirstListenComplete;

    const onButtonClick = React.useCallback(() => {
      const mode = whoa ? 'whoa' : model.currentStudyMode;

      switch (mode) {
        case 'study':
          navigateToStudy(
            chapter,
            PlayerMode.STUDY,
            chapter.currentMillis,
            viaDetail
          );
          break;

        case 'listen':
          navigateToStudy(
            chapter,
            PlayerMode.FLUENT_LISTEN,
            chapter.currentMillis,
            viaDetail
          );

          const { userSettings } = AppFactory.root.userManager.userData;
          userSettings.dismissTip('onboardingComplete');
          // story.progress.markCurrentChapterComplete();

          break;

        case 'whoa':
          model.setCurrentStudyMode('study');
          break;

        case 'completed':
          chapter.markComplete();
          // if (chapter.isLastOne) {
          //   maybePresentEndOfStoryCtaDialog();
          // }
          break;
      }
    }, [chapter, model, navigateToStudy, whoa, viaDetail]);

    const parent = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      parent.current &&
        autoAnimate(parent.current, {
          duration: 150,
          easing: 'ease-out',
        });
    }, [parent]);

    const buttonMode = whoa ? 'whoa' : model.currentStudyMode;
    const isPlayerAction = ['study', 'listen'].includes(buttonMode);
    const midListen = chapter.currentMillis > 0 && isPlayerAction;
    if (!presentation) {
      presentation = isPlayerAction
        ? buttonMode === 'study'
          ? 'tealProgress'
          : 'grayDarkProgress'
        : 'contextual';
    }

    return (
      <Button
        leftIcon={whoa ? <ArrowLeftIcon /> : null}
        // presentation="contextual"
        presentation={presentation}
        onClick={onButtonClick}
        label={getButtonLabel(buttonMode, midListen)}
        size="extraLarge"
        css={{
          $$currentProgress: midListen ? `${chapter.currentPercentage}%` : '0%',
        }}
        fullWidth
      />
    );
  }
);
